import {
  SurveyCaption,
  SurveysResponsesQuestionsQuery,
  SurveysResponsesQuestionsQueryVariables,
} from '@/graphql/generated/graphql';
import LikertScale, { Answer } from '../likert-chart';
import { FETCH_SURVEY_RESPONSE_QUESTIONS } from '@/graphql/queries/survey/survey-dashboard';
import { useQuery } from '@apollo/client';
import { useSurveyDashboardStore } from '../../store/useSurveyDashboard.store';
import React, { useEffect } from 'react';
import { classNames } from '@/utils/class-names';

export default function SurveysResponsesQuestions() {
  const {
    selectedSchoolsIds: selectedSchools,
    isEmpty,
    currentSurvey,
    currentTab,
    selectedComponent,
    page,
    updateCaptions,
    updateIsEmpty,
  } = useSurveyDashboardStore();
  const { data, loading } = useQuery<SurveysResponsesQuestionsQuery, SurveysResponsesQuestionsQueryVariables>(
    FETCH_SURVEY_RESPONSE_QUESTIONS,
    {
      variables: {
        surveyId: currentSurvey?.id ? Number(currentSurvey.id) : 0,
        schoolIds: selectedSchools.length ? selectedSchools.map(Number) : null,
      },
      fetchPolicy: 'cache-first',
    },
  );

  const questions = data?.surveysResponsesQuestions?.data || [];

  useEffect(() => {
    if (currentTab === 'survey-responses' && selectedComponent === page?.toggle.questions) {
      updateIsEmpty(data?.surveysResponsesQuestions?.data?.length === 0 || !data);
      updateCaptions(data?.surveysResponsesQuestions?.captions as SurveyCaption[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, data, loading, page?.toggle.questions, selectedComponent, selectedSchools]);

  // Group questions by uniqueId
  const groupedQuestions = questions?.reduce(
    (acc, question) => {
      const uniqueId = question?.uniqueId ?? 'unknown';
      if (!acc[uniqueId]) {
        acc[uniqueId] = [];
      }
      acc[uniqueId].push(question);
      return acc;
    },
    {} as Record<string, typeof questions>,
  );

  if (isEmpty && !loading) {
    return (
      <div className=" col-span-12 text-center">
        <h1 className=" !text-sh-md !font-bold">{page?.notFoundMessage}</h1>
      </div>
    );
  }

  return (
    <div className="col-span-12 overflow-x-auto overflow-y-hidden">
      <div className="min-w-[1000px] divide-y">
        <table className="min-w-full">
          <colgroup>
            <col className="w-1/12" />
            <col className="w-1/12" />
            <col className="w-3/12" />
            <col className="w-1/12" />
            <col className="w-6/12" />
          </colgroup>
          <thead>
            <tr className="text-sh-xxxs font-bold text-black-100">
              <th className="px-1 py-2">{page?.tableQuestion.day}</th>
              <th className="px-1 py-2">{page?.tableQuestion.date}</th>
              <th className="px-1 py-2">{page?.tableQuestion.questions}</th>
              <th className="px-1 py-2">{page?.tableQuestion.responses}</th>
              <th className="px-1 py-2">{page?.tableQuestion.answers}</th>
            </tr>
          </thead>
          <tbody className="text-sh-xxxs font-bold text-black-100">
            {loading || !groupedQuestions
              ? // Render 3 skeleton rows during loading
                Array.from({ length: 20 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="animate-pulse">
                    <td className="px-1 py-2 text-center">
                      <div className="h-4 rounded bg-gray-300"></div>
                    </td>
                    <td className="px-1 py-2 text-center">
                      <div className="h-4 rounded bg-gray-300"></div>
                    </td>
                    <td className="px-1 py-2 text-center">
                      <div className="h-4 rounded bg-gray-300"></div>
                    </td>
                    <td className="px-1 py-2 text-center">
                      <div className="h-4 rounded bg-gray-300"></div>
                    </td>
                    <td className="px-1 py-2 text-center">
                      <div className="h-4 rounded bg-gray-300"></div>
                    </td>
                  </tr>
                ))
              : Object.entries(groupedQuestions).map(([uniqueId, questionGroup]) => {
                  const hasMultipleQuestions = questionGroup.length > 1;
                  return (
                    <React.Fragment key={uniqueId}>
                      {hasMultipleQuestions && (
                        <tr key={`${uniqueId}-header`}>
                          <td className="px-1 py-2"></td>
                          <td className="px-1 py-2"></td>
                          <td className="px-1 py-2 text-center">{questionGroup[0]?.question}</td>
                          <td className="px-1 py-2"></td>
                          <td className="px-1 py-2"></td>
                        </tr>
                      )}
                      {questionGroup.map((question, index) => {
                        const dateMonthName = question?.date
                          ? new Date(question?.date).toLocaleString('default', { month: 'short' })
                          : '';
                        const dateDayAndMonth = question?.date
                          ? new Date(question?.date).toLocaleString('default', {
                              day: '2-digit',
                              month: '2-digit',
                            })
                          : '';
                        const addBottomBorder = index === questionGroup.length - 1;
                        return (
                          <tr
                            key={`${uniqueId}-${index}`}
                            className={classNames('border-b border-[#89B4FD80]', addBottomBorder ? '' : 'border-b-0')}>
                            <td className="px-1 py-2 text-center">{dateMonthName}</td>
                            <td className="px-1 py-2 text-center">{dateDayAndMonth}</td>
                            <td className="px-1 py-2 text-center">{hasMultipleQuestions ? '' : question?.question}</td>
                            <td className="px-1 py-2 text-center">{question?.responses}</td>
                            <td className="px-1 py-2 text-center">
                              <LikertScale answers={question?.answers as Answer[]} />
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
